import { useState, type PropsWithChildren } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import { Alert } from "@/src/lib/alert";
import { border, radius, spacing, text } from "@/src/theme/tokens";
import { Text } from "@/src/components/ui/BrandText";

export function ControlDisclosure({ title = "Détails techniques", children }: PropsWithChildren<{ title?: string }>) {
  const [open, setOpen] = useState(false);
  return (
    <View style={styles.disclosure}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        onPress={() => setOpen((value) => !value)}
        style={styles.disclosureTrigger}
      >
        <Text style={styles.disclosureTitle}>{title}</Text>
        <Text style={styles.disclosureAction}>{open ? "Masquer" : "Afficher"}</Text>
      </Pressable>
      {open ? <View style={styles.disclosureContent}>{children}</View> : null}
    </View>
  );
}

export function ControlId({ label, value }: { label: string; value?: string | null }) {
  const [copied, setCopied] = useState(false);
  const normalized = value?.trim() || "Non renseigné";

  async function copy() {
    if (!value?.trim()) return;
    const clipboard = (globalThis as typeof globalThis & {
      navigator?: { clipboard?: { writeText: (text: string) => Promise<void> } };
    }).navigator?.clipboard;
    if (Platform.OS === "web" && clipboard) {
      try {
        await clipboard.writeText(value);
        setCopied(true);
        return;
      } catch {
        // A selectable value remains available when the browser blocks access.
      }
    }
    Alert.alert("Identifiant prêt à copier", "Sélectionne le texte affiché pour le copier.");
  }

  return (
    <View style={styles.idBlock}>
      <Text style={styles.idLabel}>{label}</Text>
      <View style={styles.idRow}>
        <Text selectable style={styles.idValue}>{normalized}</Text>
        {value?.trim() ? (
          <Pressable accessibilityRole="button" onPress={() => void copy()} style={styles.copyButton}>
            <Text style={styles.copyText}>{copied ? "Copié" : "Copier"}</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

export function ControlKeyValue({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.keyValue}>
      <Text style={styles.key}>{label}</Text>
      <Text style={styles.value}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  disclosure: { borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, minWidth: 0 },
  disclosureTrigger: { alignItems: "center", flexDirection: "row", gap: spacing.md, justifyContent: "space-between", paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  disclosureTitle: { color: text.secondary, fontSize: 12, fontWeight: "700" },
  disclosureAction: { color: text.brand, fontSize: 12, fontWeight: "800" },
  disclosureContent: { borderTopColor: border.subtle, borderTopWidth: 1, gap: spacing.md, padding: spacing.md },
  idBlock: { gap: spacing.xs, minWidth: 0 },
  idLabel: { color: text.muted, fontSize: 11, fontWeight: "700" },
  idRow: { alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: spacing.sm, minWidth: 0 },
  idValue: { color: text.secondary, flex: 1, fontFamily: "monospace", fontSize: 12, minWidth: 0 },
  copyButton: { borderColor: border.subtle, borderRadius: radius.sm, borderWidth: 1, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
  copyText: { color: text.brand, fontSize: 11, fontWeight: "800" },
  keyValue: { gap: spacing.xs },
  key: { color: text.muted, fontSize: 11, fontWeight: "700" },
  value: { color: text.primary, lineHeight: 21, minWidth: 0 },
});
